/**
 * Base class for instanced rendering with optional per-instance culling + LOD.
 *
 * Source of truth is `instances` + `_masterMatrices`. When cull/LOD is active,
 * visible instances are compacted per camera into dense draw slots
 * (`instanceMatrix[0..count)`), and `_drawnToLogical` maps draw slots back to
 * logical instance ids.
 */
import * as THREE from 'three';

import { AbstractEngineClass, ClassRegistry } from '../../systems/ClassRegistry.js';
import { getLatest } from '../../utils/Async.js';
import { resourceManager } from '../../utils/ResourceManager.js';
import { property } from '../../utils/serialization/decorator.js';
import { isNode } from '../../utils/Utils.js';
import { PrimitiveComponent } from '../PrimitiveComponent.js';

import type { GenericMaterialType } from '../../materials.js';
import type { EditorPropertyChangedResult } from '../../utils/EditorClassMeta.js';
import type { SceneGraphDebugNode } from '../../utils/SceneGraphUtils.js';
import type { PrimitiveComponentOptions } from '../PrimitiveComponent.js';
import type { ComponentDescriptionOptions } from '../SceneComponent.js';

export interface GeometryMaterialPair {
  geometry: THREE.BufferGeometry;
  material: GenericMaterialType;
  geometryMatrix?: THREE.Matrix4;
}

export interface InstanceTransform {
  position?: THREE.Vector3;
  rotation?: THREE.Euler;
  scale?: THREE.Vector3;
}

/** One tier in a single-geometry LOD stack. `distance` is the world-space camera distance at which this tier becomes the selected one. LOD0 is implicit (distance 0). */
export interface LODEntry {
  geometry: THREE.BufferGeometry;
  material?: GenericMaterialType;
  distance: number;
}

export interface BaseInstancedMeshComponentOptions extends PrimitiveComponentOptions {
  maxInstances?: number;
  availableInstances?: number;
  uvScaleTilesPerUnit?: number;
  instances?: InstanceTransform[];
  isWorldSpace?: boolean;
  colors?: THREE.Color[] | THREE.Color | Map<number, THREE.Color>;
  updatePhysicsOnInstanceTransform?: boolean;
  instanceScale?: THREE.Vector3;
  instanceRotation?: THREE.Vector3;
  instanceOffset?: THREE.Vector3;
  instanceDensity?: number;
  instanceScaleDelta?: number;
  instanceRotationDelta?: THREE.Vector3;
  instanceOffsetDelta?: THREE.Vector3;
  cullDistance?: number;
  perInstanceFrustumCulling?: boolean;
}

// Reusable scratch objects (avoid per-frame allocations).
const _tmpMat = new THREE.Matrix4();
const _tmpMat2 = new THREE.Matrix4();
const _tmpInv = new THREE.Matrix4();
const _tmpBox = new THREE.Box3();
const _tmpSphere = new THREE.Sphere();
const _tmpVec = new THREE.Vector3();
const _tmpPos = new THREE.Vector3();
const _tmpQuat = new THREE.Quaternion();
const _tmpEuler = new THREE.Euler();
const _tmpScale = new THREE.Vector3();
const _frustum = new THREE.Frustum();
const _tmpProj = new THREE.Matrix4();
const _tmpColor = new THREE.Color();

/** Sentinel stored in `_lastClassification` for instances culled in the previous frame. */
const LOD_CULLED = 255;

/** Shared no-op assigned to `InstancedMesh.onBeforeRender` when we uninstall our hook. */
const _noopOnBeforeRender = (() => { /* noop */ }) as THREE.Object3D['onBeforeRender'];

@AbstractEngineClass('BaseInstancedMeshComponent')
export abstract class BaseInstancedMeshComponent extends PrimitiveComponent {

  // --- LOD-aware storage ------------------------------------------------------
  /** [lodIndex][submeshIndex] -> geometry/material pair. LOD0 has index 0. */
  protected _geometryMaterialPairsByLOD: GeometryMaterialPair[][] = [];
  /** [lodIndex][submeshIndex] -> underlying THREE.InstancedMesh (or null when this LOD has fewer submeshes). */
  protected _instancedMeshesByLOD: Array<Array<THREE.InstancedMesh | null>> = [];
  /** Squared camera distance at which each LOD becomes the selected tier (LOD0 is always 0). */
  private _lodDistancesSq: Float32Array | null = null;

  // --- CPU master caches -------------------------------------------
  private _masterMatrices: Float32Array | null = null;
  private _masterWorldPositions: Float32Array | null = null;
  private _masterWorldPositionsDirty: boolean = true;
  /**
   * Per-instance cached max linear scale derived from the master matrix's upper-3x3
   * column lengths. Multiplied by the LOD0 geometry bounding radius in the hot loop
   * to form the conservative per-instance culling sphere — avoids recomputing 9
   * multiplies + 1 sqrt per instance every frame for static transforms.
   */
  private _masterMaxScales: Float32Array | null = null;
  /** Snapshot of `this.matrixWorld` at the last world-position cache recompute (for detecting component movement). */
  private _cachedMatrixWorld: THREE.Matrix4 = new THREE.Matrix4();

  // --- Cull/LOD runtime state ------------------------------------
  private _cullAndLodActive: boolean = false;
  private _onBeforeRenderInstalled: boolean = false;
  /** Set by the editor brush to revert to uncompacted mode during a stroke. */
  private _suspendCullAndLOD: boolean = false;
  /** [lodIndex][submeshIndex] -> drawn-slot -> logical instance id. */
  private _drawnToLogical: Uint32Array[][] = [];
  /**
   * [lodIndex][submeshIndex] -> current-frame draw counter, reused across frames to
   * avoid allocating jagged arrays inside the compaction hot path. Shape is mirrored
   * from `_instancedMeshesByLOD` at populate time and reset in-place at the start of
   * each `_runCullAndLODPass`.
   */
  private _counters: Uint32Array[] = [];
  /**
   * Previous-frame draw count for each (lod, submesh), used so we can skip
   * `instanceMatrix.needsUpdate = true` entirely on LODs that were and remain empty.
   */
  private _prevDrawn: Uint32Array[] = [];
  /** Per-instance LOD bin (or LOD_CULLED). */
  private _lastClassification: Uint8Array | null = null;
  /** True when instance colors were set via `setInstanceColors` and need replication into drawn slots. */
  private _colorsDirty: boolean = true;
  /** True when at least one LOD's InstancedMesh uses `instanceColor`. */
  private _hasInstanceColors: boolean = false;

  private shouldRefreshPhysics: boolean = false;

  @property()
  public maxInstances: number = 1000;

  @property()
  public uvScaleTilesPerUnit: number = 1;

  @property({hidden: true}) // Not desired to show in properties panel
  public instances: InstanceTransform[] = [];

  @property()
  public isWorldSpace: boolean = false;

  @property()
  public colors: THREE.Color[] | THREE.Color | Map<number, THREE.Color> | null = null;

  @property()
  public updatePhysicsOnInstanceTransform: boolean = false;

  @property()
  public instanceScale: THREE.Vector3 = new THREE.Vector3(1, 1, 1);

  @property()
  public instanceRotation: THREE.Vector3 = new THREE.Vector3(0, 0, 0);

  @property()
  public instanceOffset: THREE.Vector3 = new THREE.Vector3(0, 0, 0);

  @property()
  public instanceDensity: number = 1.0;

  @property()
  public instanceScaleDelta: number = 0.0;

  @property()
  public instanceRotationDelta: THREE.Vector3 = new THREE.Vector3(0, 0, 0);

  @property()
  public instanceOffsetDelta: THREE.Vector3 = new THREE.Vector3(0, 0, 0);

  // --- Opt-in cull/LOD properties --------------------------------------------

  /** Cull instances past this world-space distance from the camera. `0` disables the feature. */
  @property()
  public cullDistance: number = 0;

  /** Enable per-instance frustum culling in the compaction pass. */
  @property()
  public perInstanceFrustumCulling: boolean = true;

  public override initialize(options?: BaseInstancedMeshComponentOptions): void {
    super.initialize(options);
    this.maxInstances = options?.maxInstances ?? this.maxInstances;
    this.uvScaleTilesPerUnit = options?.uvScaleTilesPerUnit ?? this.uvScaleTilesPerUnit;
    this.instances = options?.instances ?? this.instances;
    this.isWorldSpace = options?.isWorldSpace ?? this.isWorldSpace;
    this.colors = options?.colors ?? this.colors;
    this.updatePhysicsOnInstanceTransform = options?.updatePhysicsOnInstanceTransform ?? this.updatePhysicsOnInstanceTransform;
    this.instanceScale = options?.instanceScale ?? this.instanceScale;
    this.instanceRotation = options?.instanceRotation ?? this.instanceRotation;
    this.instanceOffset = options?.instanceOffset ?? this.instanceOffset;
    this.instanceDensity = options?.instanceDensity ?? this.instanceDensity;
    this.instanceScaleDelta = options?.instanceScaleDelta ?? this.instanceScaleDelta;
    this.instanceRotationDelta = options?.instanceRotationDelta ?? this.instanceRotationDelta;
    this.instanceOffsetDelta = options?.instanceOffsetDelta ?? this.instanceOffsetDelta;
    this.cullDistance = options?.cullDistance ?? this.cullDistance;
    this.perInstanceFrustumCulling = options?.perInstanceFrustumCulling ?? this.perInstanceFrustumCulling;
    this.updateMatrixWorld(true);
  }

  public override tickPrePhysics(deltaTime: number): void {
    if (this.shouldRefreshPhysics) {
      this.refreshPhysics();
      this.shouldRefreshPhysics = false;
    }
    super.tickPrePhysics(deltaTime);
  }

  /**
   * Populate the component with geometry/material pairs. Accepts either a single
   * row (legacy single-LOD) or a 2-D array indexed as `[lodIndex][submeshIndex]`.
   * When multi-LOD, `lodDistances` must supply the distance threshold for each LOD
   * tier (LOD0 should typically be 0).
   */
  protected populateInstances(
    geometriesAndMaterials: GeometryMaterialPair[] | GeometryMaterialPair[][],
    lodDistances?: number[]
  ): void {
    const isMultiLod = geometriesAndMaterials.length > 0 && Array.isArray(geometriesAndMaterials[0]);
    const lodPairs: GeometryMaterialPair[][] = isMultiLod
      ? (geometriesAndMaterials as GeometryMaterialPair[][])
      : [geometriesAndMaterials as GeometryMaterialPair[]];
    const distances = lodDistances ?? [0];

    if (isMultiLod && distances.length !== lodPairs.length) {
      console.warn(`BaseInstancedMeshComponent: lodDistances length (${distances.length}) does not match number of LOD tiers (${lodPairs.length}); using LOD0 only`);
      lodPairs.length = 1;
    }

    const maxInstances = this.getMaxInstances();
    const availableInstances = this.availableInstances;

    this._geometryMaterialPairsByLOD = lodPairs;
    this._lodDistancesSq = new Float32Array(lodPairs.length);
    for (let i = 0; i < lodPairs.length; i++) {
      const d = distances[i] ?? 0;
      this._lodDistancesSq[i] = d * d;
    }

    if (lodPairs.length === 0 || lodPairs[0].length === 0) {
      console.warn('BaseInstancedMeshComponent.populateInstances called with empty geometry-material pairs');
    }

    // Buffer capacity should be the maxInstances guard
    this.initializeInstancedMeshesLOD(lodPairs, maxInstances);

    // Allocate CPU caches sized to maxInstances.
    this._masterMatrices = new Float32Array(maxInstances * 16);
    this._masterWorldPositions = new Float32Array(maxInstances * 3);
    this._masterMaxScales = new Float32Array(maxInstances);
    this._masterWorldPositionsDirty = true;
    this._cachedMatrixWorld.identity();
    this._lastClassification = new Uint8Array(maxInstances);
    this._drawnToLogical = this._instancedMeshesByLOD.map(row => row.map(() => new Uint32Array(maxInstances)));
    // Reuse per-frame counter storage; Uint32Array is plenty (counts can never exceed maxInstances).
    this._counters = this._instancedMeshesByLOD.map(row => new Uint32Array(row.length));
    this._prevDrawn = this._instancedMeshesByLOD.map(row => new Uint32Array(row.length));

    // Clamp `instances` to capacity.
    if (this.instances.length > maxInstances) {
      this.instances = this.instances.slice(0, maxInstances);
    }

    // Seed master matrices + GPU buffers from instances[].
    const effectiveCount = this.instances.length;
    if (effectiveCount > 0) {
      const sourceInstances = this.instances.slice();
      // setInstanceTransforms respects `isWorldSpace`; since `this.instances` may have been
      // populated from a world-space options blob, preserve that semantic for seeding only.
      this.setInstanceTransforms(sourceInstances, this.isWorldSpace);
    } else {
      this._setAllMeshCounts(0);
    }

    this._applyInstanceColors();
    this._updateCullAndLODActivation();
    this._recomputeBounds();

    console.log(
      'BaseInstancedMeshComponent populated:',
      'availableInstances:', availableInstances,
      'maxInstances:', maxInstances,
      'actuallyShown:', Math.min(availableInstances, maxInstances),
      'lodCount:', this._instancedMeshesByLOD.length,
      'submeshCount:', this._instancedMeshesByLOD[0]?.length ?? 0,
      'isWorldSpace:', this.isWorldSpace
    );
  }

  protected repopulateInstances() {
    if (this._geometryMaterialPairsByLOD.length === 0) return;
    const distances = this._lodDistancesSq
      ? Array.from(this._lodDistancesSq, d2 => Math.sqrt(d2))
      : undefined;
    this.populateInstances(this._geometryMaterialPairsByLOD, distances);
  }

  public requestUpdatePhysics(): void {
    this.shouldRefreshPhysics = true;
  }

  protected override shouldSyncToPhysics(): boolean {
    // if this.shouldRefreshPhysics is true, physics will be re-created in tickPrePhysics,
    // also the instances won't move if the instanced mesh component itself moves
    // so no need to update physics by syncToPhysics
    return false;
  }

  /**
   * Create (LOD, submesh) InstancedMesh grid at `maxInstances` capacity and attach to the component.
   */
  protected initializeInstancedMeshesLOD(pairsByLOD: GeometryMaterialPair[][], maxInstances: number): void {
    this.clearInstancedMeshes();

    let maxSubmeshCount = 0;
    for (const pairRow of pairsByLOD) {
      const row: Array<THREE.InstancedMesh | null> = [];
      for (const pair of pairRow) {
        const instancedMesh = new THREE.InstancedMesh(pair.geometry, undefined, maxInstances);
        instancedMesh.userData.geometryMatrix = pair.geometryMatrix;
        instancedMesh.userData.geometryMatrixInv = pair.geometryMatrix ? pair.geometryMatrix.clone().invert() : undefined;
        instancedMesh.count = 0;
        // NOTE: We intentionally leave `instanceMatrix.usage` at its default `StaticDrawUsage`
        // and always upload the full buffer via a version-bump-only `needsUpdate = true` in the
        // compaction finalize step. Empirically, on ANGLE/D3D11 (Chromium on Windows) this is
        // dramatically faster than `DynamicDrawUsage` + partial `bufferSubData` via
        // `addUpdateRange`, because ANGLE fast-paths full-buffer replacement as
        // `MAP_WRITE_DISCARD` (effective orphaning) whereas partial updates become
        // serializing `UpdateSubresource` calls that stall on in-flight GPU reads.
        row.push(instancedMesh);
        this.setupInstancedMesh(instancedMesh);

        if (!isNode()) {
          this.setMaterialForMesh(instancedMesh, pair.material);
        }
      }
      maxSubmeshCount = Math.max(maxSubmeshCount, row.length);
      this._instancedMeshesByLOD.push(row);
    }

    // Normalize all LOD rows so [lod][submesh] indices are stable across tiers.
    for (const row of this._instancedMeshesByLOD) {
      while (row.length < maxSubmeshCount) row.push(null);
    }
  }

  private setupInstancedMesh(instancedMesh: THREE.InstancedMesh): void {
    instancedMesh.setTransient(true);
    this.bindObject3DProperties(instancedMesh, ['castShadow', 'receiveShadow', 'frustumCulled']);
    this.add(instancedMesh);
    instancedMesh.updateMatrixWorld(true);

    // TODO: Fix me
    // if (this.options.uvScaleTilesPerUnit) {
    //   MaterialUtils.scaleUVsBySize(instancedMesh, this.options.uvScaleTilesPerUnit);
    // }
  }

  private setMaterialForMesh(instancedMesh: THREE.InstancedMesh, material: GenericMaterialType): void {
    // Use getLatest to prevent race conditions when material changes rapidly
    getLatest(
      instancedMesh,
      'material',
      () => resourceManager.loadGenericMaterial(material)
    ).then((loadedMaterial) => {
      instancedMesh.material = loadedMaterial ?? new THREE.MeshStandardMaterial();
    });
  }

  private clearInstancedMeshes(): void {
    for (const row of this._instancedMeshesByLOD) {
      for (const mesh of row) {
        if (!mesh) continue;
        mesh.removeFromParent();
      }
    }
    this._instancedMeshesByLOD = [];
    this._drawnToLogical = [];
    this._onBeforeRenderInstalled = false;
  }

  /** Return the LOD0 submeshes. For callers that don't care about LODs (e.g. physics). */
  public getInstancedMeshes(): THREE.InstancedMesh[] {
    const row = this._instancedMeshesByLOD[0] ?? [];
    return row.filter((m): m is THREE.InstancedMesh => m !== null);
  }

  /**
   * Expand LOD0 instanced submeshes into detached world-space meshes for NavMesh baking.
   * Each logical instance × submesh becomes one mesh with `matrixWorld` set for Recast input.
   */
  public collectNavigationWorldMeshes(): THREE.Mesh[] {
    const result: THREE.Mesh[] = [];
    const instanceCount = this.getInstanceCount();
    if (instanceCount === 0) {
      return result;
    }

    const submeshes = this.getInstancedMeshes();
    if (submeshes.length === 0) {
      return result;
    }

    this.updateMatrixWorld(true);

    for (const instancedMesh of submeshes) {
      const geometry = instancedMesh.geometry;
      const positionAttribute = geometry.attributes.position as THREE.BufferAttribute | undefined;
      if (!positionAttribute || positionAttribute.itemSize !== 3) {
        continue;
      }

      for (let instanceId = 0; instanceId < instanceCount; instanceId++) {
        this._readMasterMatrix(instanceId, _tmpMat);
        const geometryMatrix = instancedMesh.userData.geometryMatrix as THREE.Matrix4 | undefined;
        if (geometryMatrix) {
          _tmpMat.multiply(geometryMatrix);
        }
        _tmpMat.premultiply(this.matrixWorld);

        const mesh = new THREE.Mesh(geometry, instancedMesh.material);
        mesh.setTransient(true);
        mesh.matrixAutoUpdate = false;
        mesh.matrixWorldAutoUpdate = false;
        mesh.matrixWorld.copy(_tmpMat);
        result.push(mesh);
      }
    }

    return result;
  }

  public getInstancedMesh(index: number = 0): THREE.InstancedMesh | undefined {
    const mesh = (this._instancedMeshesByLOD[0] ?? [])[index];
    return mesh ?? undefined;
  }

  public getMaterials(): (THREE.Material | THREE.Material[])[] {
    return (this._instancedMeshesByLOD[0] ?? [])
      .filter((m): m is THREE.InstancedMesh => m !== null)
      .map(mesh => mesh.material);
  }

  public getMaterial(index: number = 0): THREE.Material | THREE.Material[] | undefined {
    const mesh = (this._instancedMeshesByLOD[0] ?? [])[index];
    return mesh?.material;
  }

  public setGeometry(geometry: THREE.BufferGeometry, index: number = 0): void {
    // Apply geometry to every LOD's matching submesh.
    for (const row of this._instancedMeshesByLOD) {
      const mesh = row[index];
      if (!mesh) continue;
      mesh.geometry = geometry;
    }
  }

  public setMaterial(material: GenericMaterialType, index: number = 0): void {
    for (const row of this._instancedMeshesByLOD) {
      const mesh = row[index];
      if (!mesh) continue;
      this.setMaterialForMesh(mesh, material);
    }
  }

  public get availableInstances(): number {
    return this.instances.length;
  }

  /** Logical instance count. Always equal to `this.instances.length`. */
  public getInstanceCount(): number {
    return this.instances.length;
  }

  public getMaxInstances(): number {
    return this.maxInstances;
  }

  private getBufferCapacity(): number {
    if (this._masterMatrices) return this._masterMatrices.length / 16;
    if (this._instancedMeshesByLOD.length === 0 || this._instancedMeshesByLOD[0].length === 0) return 0;
    const anchor = this._instancedMeshesByLOD[0].find((m): m is THREE.InstancedMesh => m !== null);
    return anchor?.instanceMatrix.count ?? 0;
  }

  /**
   * Shared mutator: stores the transform in `this.instances` (component-local space),
   * updates `_masterMatrices[instanceId]`, and — when the compaction pass is inactive —
   * writes to every InstancedMesh slot directly. The compaction pass re-writes drawn
   * slots per-frame when active.
   */
  private setInstanceTransformCore(instanceId: number, transform: InstanceTransform, isWorldSpace: boolean): void {
    const bufferCapacity = this.getBufferCapacity();
    if (instanceId < 0 || instanceId >= bufferCapacity) return;

    // Convert incoming transform to component-local space.
    const localTransform = this._toComponentLocalTransform(transform, isWorldSpace);
    this.instances[instanceId] = localTransform;

    // Compose component-local matrix (pre-geometryMatrix) and cache it.
    _tmpEuler.copy(localTransform.rotation ?? _tmpEuler.set(0, 0, 0));
    _tmpQuat.setFromEuler(_tmpEuler);
    _tmpMat.compose(
      localTransform.position ?? _tmpVec.set(0, 0, 0),
      _tmpQuat,
      localTransform.scale ?? _tmpScale.set(1, 1, 1)
    );
    this._writeMasterMatrix(instanceId, _tmpMat);
    this._masterWorldPositionsDirty = true;

    // Uncompacted path: write directly into every (LOD, submesh) GPU slot.
    if (!this._cullAndLodActive) {
      for (const row of this._instancedMeshesByLOD) {
        for (const mesh of row) {
          if (!mesh) continue;
          _tmpMat2.copy(_tmpMat);
          if (mesh.userData.geometryMatrix) _tmpMat2.multiply(mesh.userData.geometryMatrix);
          mesh.setMatrixAt(instanceId, _tmpMat2);
          mesh.instanceMatrix.needsUpdate = true;
        }
      }
    }

    if (this.updatePhysicsOnInstanceTransform) {
      this.requestUpdatePhysics();
    }
  }

  private _toComponentLocalTransform(transform: InstanceTransform, isWorldSpace: boolean): InstanceTransform {
    if (!isWorldSpace) {
      return {
        position: transform.position ? transform.position.clone() : new THREE.Vector3(),
        rotation: transform.rotation ? transform.rotation.clone() : new THREE.Euler(),
        scale: transform.scale ? transform.scale.clone() : new THREE.Vector3(1, 1, 1),
      };
    }
    this.updateMatrixWorld(true);
    const q = new THREE.Quaternion().setFromEuler(transform.rotation ?? new THREE.Euler());
    const worldMat = new THREE.Matrix4().compose(
      transform.position ?? new THREE.Vector3(),
      q,
      transform.scale ?? new THREE.Vector3(1, 1, 1)
    );
    _tmpInv.copy(this.matrixWorld).invert();
    worldMat.premultiply(_tmpInv);
    const pos = new THREE.Vector3();
    const outQuat = new THREE.Quaternion();
    const outScale = new THREE.Vector3();
    worldMat.decompose(pos, outQuat, outScale);
    return {
      position: pos,
      rotation: new THREE.Euler().setFromQuaternion(outQuat),
      scale: outScale,
    };
  }

  private _writeMasterMatrix(instanceId: number, mat: THREE.Matrix4): void {
    if (!this._masterMatrices) return;
    const base = instanceId * 16;
    const e = mat.elements;
    const m = this._masterMatrices;
    for (let i = 0; i < 16; i++) m[base + i] = e[i];

    // Cache the conservative max linear scale of the upper-3x3 so the per-frame
    // frustum-sphere test doesn't have to recompute it from mm[] for every instance.
    if (this._masterMaxScales) {
      const sx2 = e[0] * e[0] + e[1] * e[1] + e[2] * e[2];
      const sy2 = e[4] * e[4] + e[5] * e[5] + e[6] * e[6];
      const sz2 = e[8] * e[8] + e[9] * e[9] + e[10] * e[10];
      this._masterMaxScales[instanceId] = Math.sqrt(Math.max(sx2, sy2, sz2));
    }

  }

  private _readMasterMatrix(instanceId: number, out: THREE.Matrix4): void {
    if (!this._masterMatrices) {
      out.identity();
      return;
    }
    const base = instanceId * 16;
    const e = out.elements;
    const m = this._masterMatrices;
    for (let i = 0; i < 16; i++) e[i] = m[base + i];
  }

  /** Swap master matrix rows a and b (used by removeInstance's swap-last trick). */
  private _swapMasterMatrices(a: number, b: number): void {
    if (!this._masterMatrices || a === b) return;
    const aBase = a * 16;
    const bBase = b * 16;
    const m = this._masterMatrices;
    let tmp: number;
    for (let i = 0; i < 16; i++) {
      tmp = m[aBase + i];
      m[aBase + i] = m[bBase + i];
      m[bBase + i] = tmp;
    }
    // Keep the scale cache coherent with the matrix swap so per-instance cull
    // radii follow the logical instance, not the slot.
    if (this._masterMaxScales) {
      const t = this._masterMaxScales[a];
      this._masterMaxScales[a] = this._masterMaxScales[b];
      this._masterMaxScales[b] = t;
    }

  }

  public setInstanceTransform(instanceId: number, transform: InstanceTransform, isWorldSpace: boolean = false): void {
    this.setInstanceTransformCore(instanceId, transform, isWorldSpace);
    this._recomputeBounds();
  }

  public setInstanceTransforms(transforms: InstanceTransform[], isWorldSpace: boolean = false): void {
    const bufferCapacity = this.getBufferCapacity();
    const count = Math.min(transforms.length, bufferCapacity);
    console.log(`setInstanceTransforms: setting ${count} transforms (bufferCapacity: ${bufferCapacity}, worldSpace: ${isWorldSpace})`);

    this.instances.length = count;
    for (let i = 0; i < count; i++) {
      this.setInstanceTransformCore(i, transforms[i], isWorldSpace);
    }

    // Uncompacted draw count reflects logical count. Compaction pass overrides this when active.
    if (!this._cullAndLodActive) {
      this._setAllMeshCounts(count);
    }
    this._recomputeBounds();
    console.log(`Instance count after setInstanceTransforms: ${count}`);
  }

  public getInstanceTransform(instanceId: number, isWorldSpace: boolean = false): InstanceTransform | null {
    if (instanceId < 0 || instanceId >= this.instances.length) return null;
    const local = this.instances[instanceId];
    if (!isWorldSpace) {
      return {
        position: local.position ? local.position.clone() : new THREE.Vector3(),
        rotation: local.rotation ? local.rotation.clone() : new THREE.Euler(),
        scale: local.scale ? local.scale.clone() : new THREE.Vector3(1, 1, 1),
      };
    }
    this.updateMatrixWorld(true);
    this._readMasterMatrix(instanceId, _tmpMat);
    _tmpMat.premultiply(this.matrixWorld);
    const pos = new THREE.Vector3();
    const q = new THREE.Quaternion();
    const scale = new THREE.Vector3();
    _tmpMat.decompose(pos, q, scale);
    return {
      position: pos,
      rotation: new THREE.Euler().setFromQuaternion(q),
      scale,
    };
  }


  public getInstancesTransforms(isWorldSpace: boolean = false): InstanceTransform[] {
    const instanceCount = this.getInstanceCount();
    const transforms: InstanceTransform[] = [];

    for (let i = 0; i < instanceCount; i++) {
      const transform = this.getInstanceTransform(i, isWorldSpace);
      if (transform) {
        transforms.push(transform);
      }
    }

    return transforms;
  }

  public addInstance(transform: InstanceTransform, isWorldSpace: boolean = false): number {
    const currentCount = this.instances.length;
    const bufferCapacity = this.getBufferCapacity();

    if (currentCount >= bufferCapacity) {
      console.warn(`Cannot add instance: buffer capacity (${bufferCapacity}) reached`);
      return -1;
    }

    const newInstanceId = currentCount;
    this.setInstanceTransformCore(newInstanceId, transform, isWorldSpace);

    if (!this._cullAndLodActive) {
      this._setAllMeshCounts(currentCount + 1);
    }

    this._recomputeBounds();
    return newInstanceId;
  }

  public addInstances(transforms: InstanceTransform[], isWorldSpace: boolean = false): number[] {
    const currentCount = this.instances.length;
    const bufferCapacity = this.getBufferCapacity();
    const availableSlots = bufferCapacity - currentCount;
    const addCount = Math.min(transforms.length, availableSlots);

    if (addCount < transforms.length) {
      console.warn(`Cannot add all instances: only ${availableSlots} slots available, requested ${transforms.length}`);
    }

    const addedIndices: number[] = [];
    for (let i = 0; i < addCount; i++) {
      const newInstanceId = currentCount + i;
      this.setInstanceTransformCore(newInstanceId, transforms[i], isWorldSpace);
      addedIndices.push(newInstanceId);
    }

    if (!this._cullAndLodActive) {
      this._setAllMeshCounts(currentCount + addCount);
    }
    this._recomputeBounds();
    return addedIndices;
  }

  public updateInstance(instanceId: number, transform: InstanceTransform, isWorldSpace: boolean = false): boolean {
    if (instanceId < 0 || instanceId >= this.instances.length) {
      return false;
    }
    this.setInstanceTransformCore(instanceId, transform, isWorldSpace);
    this._recomputeBounds();
    return true;
  }

  public updateInstances(updates: Array<{instanceId: number, transform: InstanceTransform}>, isWorldSpace: boolean = false): number {
    let updatedCount = 0;
    const currentCount = this.instances.length;
    for (const update of updates) {
      if (update.instanceId >= 0 && update.instanceId < currentCount) {
        this.setInstanceTransformCore(update.instanceId, update.transform, isWorldSpace);
        updatedCount++;
      }
    }
    if (updatedCount > 0) this._recomputeBounds();
    return updatedCount;
  }

  public removeInstance(instanceId: number): boolean {
    const currentCount = this.instances.length;
    if (instanceId < 0 || instanceId >= currentCount) {
      return false;
    }

    // Swap-last trick to keep indices dense. Applies to `this.instances`, `_masterMatrices`,
    // colors, and the uncompacted GPU slots (compaction pass rewrites them anyway).
    if (instanceId < currentCount - 1) {
      const lastInstanceId = currentCount - 1;

      this.instances[instanceId] = this.instances[lastInstanceId];
      this._swapMasterMatrices(instanceId, lastInstanceId);

      if (!this._cullAndLodActive) {
        for (const row of this._instancedMeshesByLOD) {
          for (const mesh of row) {
            if (!mesh) continue;
            const lastMatrix = new THREE.Matrix4();
            mesh.getMatrixAt(lastInstanceId, lastMatrix);
            mesh.setMatrixAt(instanceId, lastMatrix);

            if (mesh.instanceColor) {
              const lastColor = new THREE.Color();
              mesh.getColorAt(lastInstanceId, lastColor);
              mesh.setColorAt(instanceId, lastColor);
            }
            mesh.instanceMatrix.needsUpdate = true;
            if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
          }
        }
      }
    }

    this.instances.length = currentCount - 1;
    if (!this._cullAndLodActive) {
      this._setAllMeshCounts(currentCount - 1);
    }
    this._masterWorldPositionsDirty = true;

    this._recomputeBounds();
    this.requestUpdatePhysics();
    return true;
  }

  public removeInstances(instanceIds: number[]): number {
    // Sort indices in descending order to avoid index shifting issues
    const sortedIds = [...instanceIds].sort((a, b) => b - a);
    let removedCount = 0;

    for (const instanceId of sortedIds) {
      if (this.removeInstance(instanceId)) {
        removedCount++;
      }
    }

    if (removedCount > 0) this._recomputeBounds();
    return removedCount;
  }

  public removeInstanceDataInRadius(center: THREE.Vector3, radius: number, isWorldSpace: boolean = true): number {
    if (this.instances.length === 0) return 0;

    const radiusSq = radius * radius;
    const matrix = new THREE.Matrix4();
    const position = new THREE.Vector3();
    const scale = new THREE.Vector3();
    const quaternion = new THREE.Quaternion();

    // Get the component's world transform for local-to-world conversion
    let componentWorldMatrix: THREE.Matrix4 | null = null;
    if (isWorldSpace) {
      this.updateMatrixWorld(true);
      componentWorldMatrix = this.matrixWorld.clone();
    }

    // Filter out instances within the radius
    const initialCount = this.instances.length;
    this.instances = this.instances.filter((instance) => {
      const euler = instance.rotation ?? new THREE.Euler();
      quaternion.setFromEuler(euler);
      matrix.compose(
        instance.position ?? new THREE.Vector3(),
        quaternion,
        instance.scale ?? new THREE.Vector3(1, 1, 1)
      );

      if (isWorldSpace && componentWorldMatrix) {
        matrix.premultiply(componentWorldMatrix);
      }

      position.setFromMatrixPosition(matrix);
      const distanceSq = position.distanceToSquared(center);

      return distanceSq > radiusSq;
    });

    const removedCount = initialCount - this.instances.length;

    if (removedCount > 0) {
      this.repopulateInstances();
    }

    return removedCount;
  }

  public clearInstanceData(): void {
    this.instances = [];
    this.repopulateInstances();
  }

  protected combineWithBaseTransform(transform: InstanceTransform): InstanceTransform {
    const baseScale = this.instanceScale;
    const baseRotation = this.instanceRotation;
    const baseOffset = this.instanceOffset;
    const scaleDelta = this.instanceScaleDelta;
    const rotationDelta = this.instanceRotationDelta;
    const offsetDelta = this.instanceOffsetDelta;

    // Combine position: base offset + transform position + random offset delta
    const position = new THREE.Vector3();
    if (transform.position) {
      position.copy(transform.position);
    }
    position.add(baseOffset);
    position.x += (Math.random() * 2 - 1) * offsetDelta.x;
    position.y += (Math.random() * 2 - 1) * offsetDelta.y;
    position.z += (Math.random() * 2 - 1) * offsetDelta.z;

    // Combine rotation: use quaternion multiplication for correct rotation composition
    // Convert from degrees to radians for baseRotation and rotationDelta (user-specified as degrees)
    // Note: transform.rotation is already a THREE.Euler in radians, so don't convert it
    const qBase = new THREE.Quaternion().setFromEuler(new THREE.Euler(
      THREE.MathUtils.degToRad(baseRotation.x),
      THREE.MathUtils.degToRad(baseRotation.y),
      THREE.MathUtils.degToRad(baseRotation.z)
    ));
    const qTransform = transform.rotation
      ? new THREE.Quaternion().setFromEuler(transform.rotation)
      : new THREE.Quaternion();
    const qDelta = new THREE.Quaternion().setFromEuler(new THREE.Euler(
      THREE.MathUtils.degToRad((Math.random() * 2 - 1) * rotationDelta.x),
      THREE.MathUtils.degToRad((Math.random() * 2 - 1) * rotationDelta.y),
      THREE.MathUtils.degToRad((Math.random() * 2 - 1) * rotationDelta.z)
    ));

    const qFinal = new THREE.Quaternion();
    qFinal.multiplyQuaternions(qBase, qTransform);
    qFinal.multiply(qDelta);

    const rotation = new THREE.Euler().setFromQuaternion(qFinal);

    // Combine scale: base scale * transform scale * (1 + random scale delta)
    const scale = new THREE.Vector3();
    scale.copy(baseScale);
    if (transform.scale) {
      scale.multiply(transform.scale);
    }
    const scaleMultiplier = 1 + (Math.random() * 2 - 1) * scaleDelta;
    scale.multiplyScalar(scaleMultiplier);

    return { position, rotation, scale };
  }

  public pushInstanceData(transform: InstanceTransform): void {
    const combinedTransform = this.combineWithBaseTransform(transform);
    this.instances.push(combinedTransform);
    this.repopulateInstances();
  }

  public getInstanceColor(instanceId: number, target?: THREE.Color): THREE.Color {
    const color = target ?? new THREE.Color();
    const mesh = this._instancedMeshesByLOD[0]?.[0];
    if (mesh?.instanceColor && instanceId >= 0 && instanceId < this.getBufferCapacity()) {
      mesh.getColorAt(instanceId, color);
    }
    return color;
  }

  public setInstanceColors(colors: THREE.Color[] | THREE.Color | Map<number, THREE.Color> | null): void {
    if (colors === this.colors) {
      return;
    }

    this.colors = colors;
    this._applyInstanceColors();
  }

  private _applyInstanceColors(): void {
    const bufferCapacity = this.getBufferCapacity();
    this._hasInstanceColors = false;

    for (const row of this._instancedMeshesByLOD) {
      for (const mesh of row) {
        if (!mesh) continue;
        if (this.colors === null) {
          // null colors means use default color for all instances, if instanceColor was ever created
          if (mesh.instanceColor) {
            const defaultColor = new THREE.Color(0xffffff);
            for (let i = 0; i < bufferCapacity; i++) {
              mesh.setColorAt(i, defaultColor);
            }
          }
        } else if (this.colors instanceof Map) {
          // Apply colors from map at specific indices
          for (const [index, color] of (this.colors as Map<number, THREE.Color>).entries()) {
            if (index >= 0 && index < bufferCapacity && color) {
              mesh.setColorAt(index, color);
            }
          }
        } else if (Array.isArray(this.colors)) {
          // Apply colors from array
          const count = Math.min((this.colors as THREE.Color[]).length, bufferCapacity);
          for (let i = 0; i < count; i++) {
            const color = (this.colors as THREE.Color[])[i];
            if (color) mesh.setColorAt(i, color);
          }
        } else {
          // Apply single color to all instances
          for (let i = 0; i < bufferCapacity; i++) {
            mesh.setColorAt(i, this.colors as THREE.Color);
          }
        }

        if (mesh.instanceColor) {
          mesh.instanceColor.needsUpdate = true;
          this._hasInstanceColors = true;
        }
      }
    }

    this._colorsDirty = this._hasInstanceColors;
  }

  public updateInstanceMatrix(): void {
    for (const row of this._instancedMeshesByLOD) {
      for (const mesh of row) {
        if (!mesh) continue;
        mesh.instanceMatrix.needsUpdate = true;
      }
    }
  }

  public override refreshPhysics(): void {
    super.refreshPhysics();
    this.shouldRefreshPhysics = false;
  }

  public override beginPlay(): void {
    super.beginPlay();
  }

  public override generateDebugNode(): SceneGraphDebugNode | null {
    const node = super.generateDebugNode()!;
    const allMeshes: THREE.InstancedMesh[] = [];
    for (const row of this._instancedMeshesByLOD) {
      for (const mesh of row) {
        if (mesh) allMeshes.push(mesh);
      }
    }
    node.data = {
      ...node.data,
      meshCount: allMeshes.length,
      lodCount: this._instancedMeshesByLOD.length,
      availableInstances: this.availableInstances,
      instanceCount: this.getInstanceCount(),
      maxInstances: this.getMaxInstances(),
      geometries: allMeshes.map(mesh => ClassRegistry.getClassOrConstructorName(mesh.geometry)),
      materials: allMeshes.map(mesh => ClassRegistry.getClassOrConstructorName(mesh.material)),
      cullDistance: this.cullDistance,
      perInstanceFrustumCulling: this.perInstanceFrustumCulling,
    };
    return node;
  }

  /**
   * Compute the component bounding box in world space. Unions each instance's geometry
   * box transformed by the instance matrix + mesh world matrix, rather than just the
   * mesh's base geometry box (which would miss displaced instances).
   */
  public override calcBoundingBox(): void {
    this._boundingBox.makeEmpty();
    if (this._instancedMeshesByLOD.length === 0) return;

    // Prefer LOD0 submeshes; other LODs are strict subsets or alternates of the same footprint.
    const row = this._instancedMeshesByLOD[0];
    if (!row || row.length === 0) return;

    const count = this.getInstanceCount();
    for (const mesh of row) {
      if (!mesh) continue;
      mesh.geometry.computeBoundingBox();
      const geomBox = mesh.geometry.boundingBox;
      if (!geomBox) continue;
      for (let i = 0; i < count; i++) {
        this._readMasterMatrix(i, _tmpMat);
        if (mesh.userData.geometryMatrix) _tmpMat.multiply(mesh.userData.geometryMatrix);
        _tmpBox.copy(geomBox).applyMatrix4(_tmpMat).applyMatrix4(mesh.matrixWorld);
        this._boundingBox.union(_tmpBox);
      }
    }
  }

  public override describe(options?: ComponentDescriptionOptions): Record<string, any> {
    const result = super.describe(options);
    if (options?.includeDetails) {
      const allMeshes: THREE.InstancedMesh[] = [];
      for (const row of this._instancedMeshesByLOD) {
        for (const mesh of row) {
          if (mesh) allMeshes.push(mesh);
        }
      }
      result.meshCount = allMeshes.length;
      result.lodCount = this._instancedMeshesByLOD.length;
      result.meshTypes = allMeshes.map(mesh => mesh.geometry.type);
      result.availableInstances = this.availableInstances;
      result.instanceCount = this.getInstanceCount();
      result.maxInstances = this.getMaxInstances();
    }
    return result;
  }

  //====================================================================================================
  // Cull/LOD compaction pass
  //====================================================================================================

  /** Recompute `_cullAndLodActive` and install/uninstall `onBeforeRender` accordingly. */
  private _updateCullAndLODActivation(): void {
    const active = this.cullDistance > 0 || this.perInstanceFrustumCulling || this._instancedMeshesByLOD.length > 1;
    this._cullAndLodActive = active;

    if (active && !this._onBeforeRenderInstalled) {
      const anchor = this._instancedMeshesByLOD[0]?.[0];
      if (anchor) {
        anchor.onBeforeRender = (renderer, _scene, camera) => this._onBeforeRenderHook(renderer, camera);
        this._onBeforeRenderInstalled = true;
      }
    } else if (!active && this._onBeforeRenderInstalled) {
      const anchor = this._instancedMeshesByLOD[0]?.[0];
      if (anchor) {
        anchor.onBeforeRender = _noopOnBeforeRender;
      }
      this._onBeforeRenderInstalled = false;
      // Restore uncompacted GPU state.
      this._restoreUncompactedGPU();
    }
  }

  /** Re-upload master matrices into GPU slots 0..n and set `count = n`. Used when deactivating compaction. */
  private _restoreUncompactedGPU(): void {
    const n = this.instances.length;
    if (!this._masterMatrices || n === 0) {
      this._setAllMeshCounts(0);
      return;
    }
    for (const row of this._instancedMeshesByLOD) {
      for (const mesh of row) {
        if (!mesh) continue;
        for (let i = 0; i < n; i++) {
          this._readMasterMatrix(i, _tmpMat);
          if (mesh.userData.geometryMatrix) _tmpMat.multiply(mesh.userData.geometryMatrix);
          mesh.setMatrixAt(i, _tmpMat);
        }
        mesh.count = n;
        mesh.instanceMatrix.needsUpdate = true;
      }
    }
  }

  private _setAllMeshCounts(count: number): void {
    for (const row of this._instancedMeshesByLOD) {
      for (const mesh of row) {
        if (!mesh) continue;
        mesh.count = count;
      }
    }
  }

  private _onBeforeRenderHook(_renderer: unknown, camera: THREE.Camera): void {
    if (!this._cullAndLodActive) return;
    if (this._suspendCullAndLOD) {
      this._restoreUncompactedGPU();
      return;
    }

    // Only run the compaction pass for the active game camera, not for
    // shadow-map or auxiliary render passes whose camera position would
    // produce incorrect LOD classifications and distance-cull decisions.
    const activeCamera = this.getWorld()?.getActiveCamera();
    if (activeCamera && camera !== activeCamera) return;

    this._runCullAndLODPass(camera);
  }

  /** Recompute cached world-space positions for all logical instances. */
  private _recomputeWorldPositionCache(): void {
    if (!this._masterMatrices || !this._masterWorldPositions) return;
    this.updateMatrixWorld(true);
    const n = this.instances.length;
    const mw = this.matrixWorld.elements;
    const mm = this._masterMatrices;
    const wp = this._masterWorldPositions;

    // World position = matrixWorld * masterMatrix * (0,0,0,1), i.e. translation column composition.
    for (let i = 0; i < n; i++) {
      const b = i * 16;
      const lx = mm[b + 12];
      const ly = mm[b + 13];
      const lz = mm[b + 14];
      wp[i * 3]     = mw[0] * lx + mw[4] * ly + mw[8]  * lz + mw[12];
      wp[i * 3 + 1] = mw[1] * lx + mw[5] * ly + mw[9]  * lz + mw[13];
      wp[i * 3 + 2] = mw[2] * lx + mw[6] * ly + mw[10] * lz + mw[14];
    }
    this._masterWorldPositionsDirty = false;
    this._cachedMatrixWorld.copy(this.matrixWorld);
  }

  private _runCullAndLODPass(camera: THREE.Camera): void {
    if (!this._masterMatrices || !this._masterWorldPositions || !this._lastClassification) return;

    // Refresh world-position cache if the component moved or any master matrix changed.
    this.updateMatrixWorld();
    if (this._masterWorldPositionsDirty || !this._cachedMatrixWorld.equals(this.matrixWorld)) {
      this._recomputeWorldPositionCache();
    }

    // Ensure camera's world matrix is up to date; `camera.position` is LOCAL (often (0,0,0)
    // because pawns parent the camera under a rootComponent), so we must derive world-space
    // position from `matrixWorld` for the per-instance distance tests to be meaningful.
    camera.updateMatrixWorld();

    const camMW = camera.matrixWorld.elements;
    const camWorldX = camMW[12];
    const camWorldY = camMW[13];
    const camWorldZ = camMW[14];

    // Build frustum in world space (only when per-instance frustum culling is enabled).
    let frustum: THREE.Frustum | null = null;
    if (this.perInstanceFrustumCulling) {
      _tmpProj.multiplyMatrices((camera as any).projectionMatrix, (camera as any).matrixWorldInverse);
      _frustum.setFromProjectionMatrix(_tmpProj);
      frustum = _frustum;
    }

    const lodCount = this._instancedMeshesByLOD.length;

    // Determine per-LOD bounding sphere radius (use LOD0 submesh 0 as representative).
    let boundingRadius = 0;
    const lod0Meshes = this._instancedMeshesByLOD[0];
    if (lod0Meshes && lod0Meshes.length > 0) {
      const lod0Mesh = lod0Meshes.find((m): m is THREE.InstancedMesh => m !== null);
      if (!lod0Mesh) return;
      const g = lod0Mesh.geometry;
      if (!g.boundingSphere) g.computeBoundingSphere();
      boundingRadius = g.boundingSphere?.radius ?? 0;
      // Apply geometryMatrix scale factor if present (conservative upper bound).
      const gm = lod0Mesh.userData.geometryMatrix as THREE.Matrix4 | undefined;
      if (gm) {
        _tmpVec.setFromMatrixScale(gm);
        boundingRadius *= Math.max(_tmpVec.x, _tmpVec.y, _tmpVec.z);
      }
    }

    // Reset per-(LOD, submesh) counters in place — these arrays are sized once at populate
    // time and kept for the lifetime of the component, so no allocation happens here.
    const counters = this._counters;
    for (let l = 0; l < lodCount; l++) {
      const row = counters[l];
      for (let s = 0; s < row.length; s++) row[s] = 0;
    }

    const cullDist = this.cullDistance;
    const cls = this._lastClassification;
    const wp = this._masterWorldPositions;
    const mm = this._masterMatrices;
    const maxScales = this._masterMaxScales;
    const distancesSq = this._lodDistancesSq;
    const n = this.instances.length;
    const meshesByLOD = this._instancedMeshesByLOD;
    const drawnToLogical = this._drawnToLogical;
    const doFrustum = frustum !== null && boundingRadius > 0 && maxScales !== null;

    // Classify each logical instance.
    for (let i = 0; i < n; i++) {
      const wx = wp[i * 3];
      const wy = wp[i * 3 + 1];
      const wz = wp[i * 3 + 2];
      const dx = wx - camWorldX;
      const dy = wy - camWorldY;
      const dz = wz - camWorldZ;
      const distSq = dx * dx + dy * dy + dz * dz;

      // Distance cull.
      if (cullDist > 0) {
        if (distSq > cullDist * cullDist) {
          cls[i] = LOD_CULLED;
          continue;
        }
      }

      // Per-instance frustum cull (sphere test) — uses the per-instance max scale cached
      // at `_writeMasterMatrix` time so the hot path does NOT touch `mm` for scale math.
      if (doFrustum) {
        _tmpSphere.center.set(wx, wy, wz);
        _tmpSphere.radius = boundingRadius * maxScales![i];
        if (!frustum!.intersectsSphere(_tmpSphere)) {
          cls[i] = LOD_CULLED;
          continue;
        }
      }

      // LOD selection.
      let lodIndex = 0;
      if (lodCount > 1 && distancesSq) {
        lodIndex = this._chooseLOD(distSq, distancesSq);
      }

      cls[i] = lodIndex;

      // Compact into every submesh of the chosen LOD.
      const row = meshesByLOD[lodIndex];
      const lodCounters = counters[lodIndex];
      const drawnRow = drawnToLogical[lodIndex];
      const srcBase = i * 16;
      for (let s = 0; s < row.length; s++) {
        const mesh = row[s];
        if (!mesh) continue;
        const slot = lodCounters[s]++;
        const dst = mesh.instanceMatrix.array as Float32Array;
        const dstBase = slot * 16;
        // Apply geometryMatrix post-multiply (masterMat * geometryMat), inlined.
        const gm = mesh.userData.geometryMatrix as THREE.Matrix4 | undefined;
        if (gm) {
          const te = _tmpMat.elements;
          te[0]  = mm[srcBase];
          te[1]  = mm[srcBase + 1];
          te[2]  = mm[srcBase + 2];
          te[3]  = mm[srcBase + 3];
          te[4]  = mm[srcBase + 4];
          te[5]  = mm[srcBase + 5];
          te[6]  = mm[srcBase + 6];
          te[7]  = mm[srcBase + 7];
          te[8]  = mm[srcBase + 8];
          te[9]  = mm[srcBase + 9];
          te[10] = mm[srcBase + 10];
          te[11] = mm[srcBase + 11];
          te[12] = mm[srcBase + 12];
          te[13] = mm[srcBase + 13];
          te[14] = mm[srcBase + 14];
          te[15] = mm[srcBase + 15];
          _tmpMat.multiply(gm);
          for (let k = 0; k < 16; k++) dst[dstBase + k] = te[k];
        } else {
          for (let k = 0; k < 16; k++) dst[dstBase + k] = mm[srcBase + k];
        }
        drawnRow[s][slot] = i;
      }
    }

    // Finalize per-mesh counts + uploads.
    // Keep full-buffer uploads (faster on common ANGLE/D3D11 paths), but skip
    // `needsUpdate` when a mesh stayed empty.
    const prevDrawn = this._prevDrawn;
    for (let l = 0; l < lodCount; l++) {
      const row = meshesByLOD[l];
      const lodCounters = counters[l];
      const lodPrev = prevDrawn[l];
      for (let s = 0; s < row.length; s++) {
        const mesh = row[s];
        if (!mesh) continue;
        const drawn = lodCounters[s];
        mesh.count = drawn;
        if (drawn > 0 || lodPrev[s] > 0) {
          mesh.instanceMatrix.needsUpdate = true;
        }
        lodPrev[s] = drawn;
      }
    }

    // Replicate colors into drawn slots if colors are in play.
    if (this._hasInstanceColors) {
      this._compactColors(counters);
    }

  }

  /** Choose LOD index for a given distance. */
  private _chooseLOD(distSq: number, distancesSq: Float32Array): number {
    const lodCount = distancesSq.length;
    // distancesSq[l] is the (squared) threshold at which LOD l "begins".
    // Walk from highest LOD down to 0.
    for (let l = lodCount - 1; l >= 0; l--) {
      if (distSq >= distancesSq[l]) return l;
    }
    return 0;
  }

  /**
   * Replicate per-instance colors into the currently drawn slots. Called during the
   * compaction pass when `_hasInstanceColors` is true, so shader-side color lookups match.
   *
   * As with `instanceMatrix`, we take the full-buffer upload path (no `addUpdateRange`)
   * because that hits the driver's fast "orphan and rewrite" route on Chromium/ANGLE.
   */
  private _compactColors(counters: Uint32Array[]): void {
    for (let l = 0; l < this._instancedMeshesByLOD.length; l++) {
      const row = this._instancedMeshesByLOD[l];
      const drawnRow = this._drawnToLogical[l];
      const lodCounters = counters[l];
      for (let s = 0; s < row.length; s++) {
        const mesh = row[s];
        if (!mesh) continue;
        if (!mesh.instanceColor) continue;
        const count = lodCounters[s];
        for (let slot = 0; slot < count; slot++) {
          const logical = drawnRow[s][slot];
          this._getLogicalColor(logical, _tmpColor);
          mesh.setColorAt(slot, _tmpColor);
        }
        if (count > 0) {
          mesh.instanceColor.needsUpdate = true;
        }
      }
    }
    this._colorsDirty = false;
  }

  private _getLogicalColor(instanceId: number, out: THREE.Color): THREE.Color {
    if (this.colors === null) {
      out.set(0xffffff);
    } else if (this.colors instanceof Map) {
      const c = (this.colors as Map<number, THREE.Color>).get(instanceId);
      if (c) out.copy(c);
      else out.set(0xffffff);
    } else if (Array.isArray(this.colors)) {
      const c = (this.colors as THREE.Color[])[instanceId];
      if (c) out.copy(c);
      else out.set(0xffffff);
    } else {
      out.copy(this.colors as THREE.Color);
    }
    return out;
  }

  /** Force every InstancedMesh to recompute its bounding sphere + box (used by THREE's own frustum cull). */
  private _recomputeBounds(): void {
    for (const row of this._instancedMeshesByLOD) {
      for (const mesh of row) {
        if (!mesh) continue;
        mesh.computeBoundingSphere();
        mesh.computeBoundingBox();
      }
    }
  }

  //====================================================================================================
  // Editor / raycast support
  //====================================================================================================

  /**
   * Translate a raycast-reported `instanceId` (a drawn-slot index) back to the logical instance id
   * it represented this frame. When compaction is inactive, drawn slots equal logical indices and
   * this is an identity map.
   */
  public getLogicalInstanceId(drawnId: number, lodIndex: number = 0, submeshIndex: number = 0): number {
    if (!this._cullAndLodActive || this._suspendCullAndLOD) return drawnId;
    const map = this._drawnToLogical[lodIndex]?.[submeshIndex];
    return map ? (map[drawnId] ?? drawnId) : drawnId;
  }

  /** @internal Editor brush hook: suspend compaction so drawn slots == logical indices during a stroke. */
  public setCullAndLODSuspended(suspended: boolean): void {
    if (this._suspendCullAndLOD === suspended) return;
    this._suspendCullAndLOD = suspended;
    if (suspended) this._restoreUncompactedGPU();
  }

  /**
   * Reconcile the compaction pass installation after directly mutating `cullDistance`,
   * or `perInstanceFrustumCulling` at runtime. Not needed when changes
   * go through the editor property pipeline.
   */
  public refreshCullAndLOD(): void {
    this._updateCullAndLODActivation();
  }

  /** Total number of LOD tiers currently allocated (1 when no extra LODs were configured). */
  public getLODCount(): number {
    return this._instancedMeshesByLOD.length;
  }

  /**
   * Read-only view of the `THREE.InstancedMesh` objects for a given LOD tier. Each entry
   * in the returned array corresponds to one submesh (for single-geometry components there
   * is exactly one). Use `mesh.count` on the returned meshes to read the current per-frame
   * draw count (post-compaction when culling/LOD is active).
   *
   * @remarks
   * Prefer this over `getInstancedMeshes()` / `getInstancedMesh()` when you need counts
   * for all LODs — those accessors intentionally only return LOD0 for backward compat
   * with LOD-unaware systems (physics, older gameplay code).
   */
  public getInstancedMeshesAtLOD(lodIndex: number): ReadonlyArray<THREE.InstancedMesh> {
    const row = this._instancedMeshesByLOD[lodIndex] ?? [];
    return row.filter((m): m is THREE.InstancedMesh => m !== null);
  }

  public setLODDistances(distances: number[]): void {
    if (!this._lodDistancesSq) return;
    if (distances.length !== this._lodDistancesSq.length) {
      console.warn(
        `BaseInstancedMeshComponent.setLODDistances: expected ${this._lodDistancesSq.length} ` +
        `distance(s) to match existing LOD tiers, got ${distances.length}; ignoring.`
      );
      return;
    }
    for (let i = 0; i < distances.length; i++) {
      const d = Math.max(0, distances[i]);
      this._lodDistancesSq[i] = d * d;
    }
  }

  //====================================================================================================
  // Editor Functions
  //====================================================================================================

  public override onEditorPropertyChanged(path: string, value: any, result: EditorPropertyChangedResult): void {
    super.onEditorPropertyChanged(path, value, result);
    if (path === 'cullDistance' || path === 'perInstanceFrustumCulling') {
      this._updateCullAndLODActivation();
      return;
    }
    this.repopulateInstances();
  }

  public override getEditorClassIcon(): string | null {
    return 'Icon_InstancedMesh';
  }
}
